import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from '@tanstack/react-router';
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarLogo,
} from '../ui/resizable-navbar';
import { AppLink } from '../shared/AppLink';
import { useLanguage } from '../../contexts/useLanguage';
import { buildHeaderNavLinks, isHeaderNavLinkActive } from './headerNavigationNavModel';

/**
 * Guest site navbar — brand on top, site nav under it (sticky).
 * Login / Book / VUM live in the floating dock; quick book stays on product cards.
 */
const WebDesignedResizableNavbar: React.FC<{
  onOffsetChange?: (offsetPx: number) => void;
}> = ({ onOffsetChange }) => {
  const { t } = useLanguage();
  const location = useLocation();
  const mobileNavRef = useRef<HTMLElement | null>(null);

  const navItems = useMemo(
    () =>
      buildHeaderNavLinks(t).map((item) => ({
        name: item.label,
        link: item.path,
      })),
    [t],
  );

  // Keep the active pill in view after route changes (narrow phones).
  useEffect(() => {
    const nav = mobileNavRef.current;
    if (!nav) return;
    const active = nav.querySelector<HTMLElement>('[data-nav-active="true"]');
    active?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <Navbar onBottomChange={onOffsetChange}>
      <NavBody className="nav-body-stacked !flex-col !items-stretch !justify-center !gap-1 !rounded-2xl !px-6 !py-3">
        <div className="relative z-20 flex w-full items-center justify-center">
          <NavbarLogo
            title="pignon rouge mokocan"
            subtitle="POURVOIRIE-pêche|chasse|chalets|loisirs"
            logoSrc="/images/PPRLOGO.PNG"
            to="/"
          />
        </div>
        <NavItems
          items={navItems}
          className="!relative !inset-auto !flex !w-full !justify-center !space-x-1"
        />
      </NavBody>

      <MobileNav className="!items-stretch !gap-0.5 !px-2 !py-1.5">
        <div className="relative z-20 flex w-full min-w-0 items-center justify-center px-0.5">
          <NavbarLogo
            title="pignon rouge mokocan"
            subtitle="POURVOIRIE-pêche|chasse|chalets|loisirs"
            logoSrc="/images/PPRLOGO.PNG"
            to="/"
          />
        </div>
        <nav
          ref={mobileNavRef}
          aria-label="Primary"
          className="relative z-20 flex w-full gap-0.5 overflow-x-auto px-0.5 pb-0.5 [scrollbar-width:none] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden"
        >
          {navItems.map((item) => {
            const active = isHeaderNavLinkActive(location.pathname, item.link);
            return (
              <AppLink
                key={item.link}
                to={item.link}
                data-nav-active={active ? 'true' : undefined}
                className={
                  active
                    ? 'min-w-0 flex-1 truncate rounded-full bg-[#3d2817]/10 px-1.5 py-1.5 text-center text-[11px] font-semibold text-[#3d2817] sm:px-2 sm:text-xs'
                    : 'min-w-0 flex-1 truncate rounded-full px-1.5 py-1.5 text-center text-[11px] font-medium text-[#3d2817]/85 sm:px-2 sm:text-xs'
                }
                title={item.name}
              >
                {item.name}
              </AppLink>
            );
          })}
        </nav>
      </MobileNav>
    </Navbar>
  );
};

export default WebDesignedResizableNavbar;
